import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";

const num = (s) => Number(String(s).replace(/\s/g, "").replace(",", "."));
const fr = (n) => (n === "" || n === null || n === undefined ? "" : String(n).replace(".", ","));

const Choix = ({ value, options, onChange, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const LigneRegle = ({ r, i, rubriques, immeubles, maj, retirer }) => (
  <div className="grid grid-cols-[1fr_1fr_36px] gap-2 sm:grid-cols-[1fr_1fr_130px_110px_110px_36px] items-center" data-testid={`regle-facture-${i}`}>
    <Choix value={r.rubrique} options={rubriques} onChange={(v) => maj(i, { rubrique: v, ...(v === "Hydro" ? {} : { etage: "" }) })} placeholder="Rubrique" testId={`regle-facture-${i}-rubrique`} />
    <Choix value={r.immeuble} options={immeubles} onChange={(v) => maj(i, { immeuble: v })} placeholder="Immeuble" testId={`regle-facture-${i}-immeuble`} />
    {r.rubrique === "Hydro"
      ? <Input value={r.etage || ""} onChange={(e) => maj(i, { etage: e.target.value })} placeholder="Étage (facultatif)" maxLength={40} className="order-2 col-span-3 h-9 sm:order-none sm:col-span-1" data-testid={`regle-facture-${i}-etage`} />
      : <span className="order-2 hidden sm:order-none sm:block" />}
    <Input value={r.min} onChange={(e) => maj(i, { min: e.target.value })} placeholder="De ($)" inputMode="decimal" className="order-2 h-9 tabular sm:order-none" data-testid={`regle-facture-${i}-min`} />
    <Input value={r.max} onChange={(e) => maj(i, { max: e.target.value })} placeholder="À ($)" inputMode="decimal" className="order-2 h-9 tabular sm:order-none" data-testid={`regle-facture-${i}-max`} />
    <Button size="icon" variant="ghost" className="order-1 h-9 w-9 text-slate-400 hover:text-red-600 sm:order-none" onClick={() => retirer(i)} title="Retirer" data-testid={`regle-facture-${i}-retirer`}><Trash2 className="h-4 w-4" /></Button>
  </div>
);

const montant = (n) => `${Number(n).toFixed(2).replace(".", ",")} $`;

const BilanRegles = ({ bilan }) => (
  <div className="mt-5 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="regles-factures-bilan">
    <p className="text-sm font-medium text-slate-700">Effet des règles</p>
    {bilan.map((b, i) => (
      <div key={i} className="text-sm" data-testid={`regle-bilan-${i}`}>
        <p className={b.rattachees ? "text-emerald-800" : "text-amber-700"} data-testid={`regle-bilan-${i}-resume`}>
          {b.rubrique} {montant(b.min)}{b.max !== b.min ? ` à ${montant(b.max)}` : ""} → {b.immeuble}{b.etage ? ` - ${b.etage}` : ""} : {b.rattachees} facture(s) rattachée(s){b.rattachees ? `, ${montant(b.total)}` : ""}
        </p>
        {b.ignorees.map((o) => (
          <p key={o.id} className="ml-4 text-xs text-slate-500" data-testid={`regle-bilan-${i}-ignoree-${o.id}`}>
            Non rattachée : {o.date} · {o.description} · {montant(o.montant)} — {o.raison}
          </p>
        ))}
      </div>
    ))}
  </div>
);

const versForm = (data) => data.regles.map((x) => ({ ...x, min: fr(x.min), max: fr(x.max) }));

export const FacturesImmeublePanel = () => {
  const [d, setD] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    api.get("/regles-factures").then((r) => setD({ ...r.data, regles: versForm(r.data) })).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!d) return null;
  const maj = (i, v) => setD({ ...d, regles: d.regles.map((r, j) => (j === i ? { ...r, ...v } : r)) });
  const retirer = (i) => setD({ ...d, regles: d.regles.filter((_, j) => j !== i) });
  const ajouter = () => setD({ ...d, regles: [...d.regles, { rubrique: "Hydro", immeuble: "", min: "", max: "" }] });
  const enregistrer = async () => {
    if (d.regles.some((r) => !r.rubrique || !r.immeuble || !String(r.min).trim())) {
      toast.error("Chaque règle doit avoir une rubrique, un immeuble et un montant"); return;
    }
    const regles = d.regles.map((r) => ({ ...r, min: num(r.min), max: num(String(r.max).trim() === "" ? r.min : r.max) }));
    if (regles.some((r) => Number.isNaN(r.min) || Number.isNaN(r.max))) {
      toast.error("Montant invalide : utilisez des chiffres, ex. 603,89"); return;
    }
    setEnvoi(true);
    try {
      const { data } = await api.put("/regles-factures", { regles });
      setD({ ...d, regles: versForm(data), bilan: data.bilan });
      const n = data.bilan.reduce((s, b) => s + b.rattachees, 0);
      toast.success(`Règles enregistrées : ${n} facture(s) rattachée(s). Le tableau de bord et les rapports en tiennent compte.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <Panel title="Factures par immeuble (Hydro, assurance, taxes, Internet)" testId="factures-immeuble-panel"
      action={<Button size="sm" variant="outline" onClick={ajouter} data-testid="regle-facture-ajouter"><Plus className="mr-1 h-4 w-4" />Ajouter une règle</Button>}>
      <p className="mb-4 text-xs text-slate-500">Les relevés Desjardins n'indiquent pas le numéro de compte du fournisseur. Pour une facture sans immeuble choisi, l'application regarde d'abord ces règles de montant (un montant exact : mettez le même dans « De » et « À », ex. 603,89). Une règle l'emporte aussi sur un immeuble choisi à la main (sauf pour une opération répartie entre plusieurs immeubles). Pour Hydro, l'étage facultatif (ex. « Étage 1 ») sépare la dépense au tableau de bord : « Hydro Saint Germain - Étage 1 » ; une facture sans règle d'étage reste dans « Hydro Saint Germain ». Sans règle, une facture sans immeuble choisi va à l'immeuble dont une facture déjà rattachée a le montant le plus proche (écart de 15 % au plus).</p>
      <div className="space-y-2">
        {d.regles.length ? d.regles.map((r, i) => <LigneRegle key={i} r={r} i={i} rubriques={d.rubriques} immeubles={d.immeubles} maj={maj} retirer={retirer} />)
          : <p className="text-sm text-slate-400" data-testid="regles-factures-vide">Aucune règle pour l'instant.</p>}
      </div>
      <Button onClick={enregistrer} disabled={envoi} className="mt-4 bg-emerald-700 hover:bg-emerald-800" data-testid="regles-factures-enregistrer">
        {envoi ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Enregistrer les règles
      </Button>
      {d.bilan?.length > 0 && <BilanRegles bilan={d.bilan} />}
    </Panel>
  );
};
